/*
 * Files workflow snapshot rows into `state.pieve.workflows`, keyed by snapshot `id`.
 *
 * The same snapshot arrives in more than one projection: the catalog list (no definition, but the linked
 * schedule/webhook/trigger facts and `hasPublished`), the draft read and the `workflow.updated` push (the
 * definition and `appMetadataById`), a version read, and the narrow PUT and step-update responses. Each is
 * a partial view of one row, so a row arriving is merged into the one held, never swapped for it. Swapping
 * is what blanked the editor: a catalog list landing after the draft replaced the draft with a row that has
 * no definition, and the canvas fell to "No steps yet." until something refetched it.
 *
 * Order is settled by the row's own clock, not by which response landed last. `revision` is the draft's
 * compare-and-swap counter and only rises when the definition is written; `updatedOn` also moves when the
 * test or the actions list is written. An incoming row older than the one held (lower revision, or the same
 * revision with an earlier `updatedOn`) only fills in fields the held row lacks, so a slow read that started
 * before a push can never put the older definition back on screen.
 */
const toTime = (value) => {
  const time = Date.parse(value || '');

  return Number.isFinite(time) ? time : null;
};

const isOlderSnapshot = (incoming, existing) => {
  const incomingRevision = Number(incoming?.revision);
  const existingRevision = Number(existing?.revision);

  if (Number.isFinite(incomingRevision) && Number.isFinite(existingRevision) && incomingRevision !== existingRevision) {
    return incomingRevision < existingRevision;
  }

  const incomingTime = toTime(incoming?.updatedOn);
  const existingTime = toTime(existing?.updatedOn);

  return incomingTime !== null && existingTime !== null && incomingTime < existingTime;
};

const mergeWorkflowSnapshotRows = (currentRows = [], incomingRows = []) => {
  const rows = Array.isArray(currentRows) ? [...currentRows] : [];
  const indexById = new Map(rows.map((row, index) => [row?.id, index]));

  for (const incoming of Array.isArray(incomingRows) ? incomingRows : []) {
    if (!incoming?.id) {
      continue;
    }

    const index = indexById.get(incoming.id);

    if (index === undefined) {
      indexById.set(incoming.id, rows.length);
      rows.push(incoming);
      continue;
    }

    const existing = rows[index];
    rows[index] = isOlderSnapshot(incoming, existing) ? { ...incoming, ...existing } : { ...existing, ...incoming };
  }

  return rows;
};

export default mergeWorkflowSnapshotRows;
